import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { LogOut, Star, Mail, TrendingUp, Building2, RefreshCw, Sparkles, Eye, Trash2, Copy, CheckCheck } from "lucide-react";

const Stat = ({ icon: Icon, label, value, suffix }) => (
  <div className="bg-white border border-border rounded-2xl p-5">
    <div className="flex items-center gap-2 text-ink/60 text-xs uppercase tracking-wider font-bold">
      <Icon className="w-4 h-4 text-forest" />{label}
    </div>
    <div className="font-display font-extrabold text-3xl mt-2 text-ink">{value}{suffix}</div>
  </div>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [parks, setParks] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [visits, setVisits] = useState(null);
  const [tab, setTab] = useState("featured");
  const [search, setSearch] = useState("");
  const [purging, setPurging] = useState(false);
  const [copied, setCopied] = useState(null); // "comma" | "newline" | null

  const loadAll = useCallback(async () => {
    try {
      const [d, ps, al, vs] = await Promise.all([
        api.adminDashboard(),
        api.searchParks({ limit: 250 }),
        api.adminEmailAlerts(),
        api.adminVisits(),
      ]);
      setDash(d);
      setParks(ps.results);
      setAlerts(al.alerts);
      setVisits(vs);
    } catch (ex) {
      if (ex?.response?.status === 401) {
        localStorage.removeItem("parkscanner_token");
        navigate("/admin/login");
      }
    }
  }, [navigate]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const logout = () => {
    localStorage.removeItem("parkscanner_token");
    localStorage.removeItem("parkscanner_admin");
    navigate("/admin/login");
  };

  const toggleFeatured = async (slug, featured) => {
    await api.adminToggleFeatured(slug, featured);
    setParks((prev) => prev.map((p) => (p.slug === slug ? { ...p, featured } : p)));
    setDash((prev) => prev ? { ...prev, featured_active: prev.featured_active + (featured ? 1 : -1) } : prev);
  };

  const purgeTestData = async () => {
    const ok = window.confirm(
      "Purge ALL email alerts, ALL transaction records, and reset every park's Featured flag?\n\nThis cannot be undone."
    );
    if (!ok) return;
    setPurging(true);
    try {
      const res = await api.adminPurgeTestData();
      alert(`Purged ✓\n• Email alerts deleted: ${res.email_alerts_deleted}\n• Transactions deleted: ${res.transactions_deleted}\n• Parks unfeatured: ${res.parks_unfeatured}`);
      await loadAll();
    } catch (ex) {
      alert("Purge failed: " + (ex?.response?.data?.detail || ex.message));
    } finally {
      setPurging(false);
    }
  };

  const filteredParks = parks.filter((p) =>
    !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.operator.toLowerCase().includes(search.toLowerCase())
  );

  const copyEmails = async (separator) => {
    const unique = Array.from(new Set(alerts.map((a) => a.email.trim().toLowerCase()).filter(Boolean)));
    const text = unique.join(separator === "newline" ? "\n" : ", ");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(separator);
      setTimeout(() => setCopied(null), 2500);
    } catch {
      // Fallback for older browsers
      const ta = document.createElement("textarea");
      ta.value = text; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
      setCopied(separator);
      setTimeout(() => setCopied(null), 2500);
    }
  };

  const uniqueEmailCount = Array.from(new Set(alerts.map((a) => a.email.trim().toLowerCase()).filter(Boolean))).length;

  return (
    <div className="min-h-screen bg-sand" data-testid="admin-dashboard">
      <header className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-display font-bold text-xl">ParkScanner</span>
            <span className="label-eyebrow">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-ink/60 hidden sm:inline">{localStorage.getItem("parkscanner_admin")}</span>
            <button onClick={loadAll} className="btn-ghost text-xs" data-testid="admin-refresh"><RefreshCw className="w-4 h-4" />Refresh</button>
            <button
              onClick={purgeTestData}
              disabled={purging}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium border border-terracotta/40 text-terracotta hover:bg-terracotta/10 disabled:opacity-50"
              data-testid="admin-purge"
              title="Wipe all test email alerts, transactions and featured flags"
            >
              <Trash2 className="w-3.5 h-3.5" />{purging ? "Purging…" : "Purge test data"}
            </button>
            <button onClick={logout} className="btn-ghost text-xs" data-testid="admin-logout"><LogOut className="w-4 h-4" />Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display font-extrabold text-4xl">Dashboard</h1>

        {!dash ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            {[1,2,3,4].map(i => <div key={i} className="h-28 rounded-2xl skeleton-shimmer" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6" data-testid="admin-stats">
            <Stat icon={Building2} label="Total parks" value={dash.parks} />
            <Stat icon={Eye} label="Total visits" value={(dash.visits_total ?? 0).toLocaleString()} />
            <Stat icon={Star} label="Featured active" value={dash.featured_active} />
            <Stat icon={Mail} label="Email alerts" value={dash.email_alerts} />
          </div>
        )}

        {dash && (
          <div className="mt-2 text-xs text-ink/60" data-testid="admin-visits-24h">
            <Eye className="w-3 h-3 inline mr-1 text-forest" />{(dash.visits_24h ?? 0).toLocaleString()} visits in the last 24h
            <span className="mx-2 text-ink/30">·</span>
            <TrendingUp className="w-3 h-3 inline mr-1 text-forest" />Organic growth mode — no paid sponsor slots
          </div>
        )}

        <div className="mt-10 flex gap-2 flex-wrap" data-testid="admin-tabs">
          {[
            { key: "featured", label: "Featured parks", icon: Star },
            { key: "visits", label: "Visits", icon: Eye },
            { key: "alerts", label: "Email alerts", icon: Mail },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${tab === key ? "bg-forest text-white" : "bg-white border border-border hover:border-forest"}`}
              data-testid={`admin-tab-${key}`}
            >
              <Icon className="w-4 h-4" />{label}
            </button>
          ))}
        </div>

        {tab === "featured" && (
          <div className="mt-6 bg-white border border-border rounded-2xl overflow-hidden">
            <div className="p-4 border-b border-border flex items-center justify-between gap-4">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search parks..."
                className="flex-1 bg-sand border border-border rounded-lg px-4 py-2 text-sm outline-none focus:border-forest"
                data-testid="admin-parks-search"
              />
              <span className="text-xs text-ink/60">{filteredParks.length} parks</span>
            </div>
            <div className="max-h-[600px] overflow-y-auto divide-y divide-border" data-testid="admin-parks-list">
              {filteredParks.slice(0, 80).map((p) => (
                <div key={p.slug} className="p-4 flex items-center gap-4">
                  <img src={p.hero_image} alt="" className="w-14 h-14 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold truncate">{p.name}</div>
                    <div className="text-xs text-ink/60">{p.operator} · {p.region}</div>
                  </div>
                  {p.featured && <Sparkles className="w-4 h-4 text-terracotta" />}
                  <button
                    onClick={() => toggleFeatured(p.slug, !p.featured)}
                    className={`text-xs rounded-full px-3 py-1.5 border font-medium ${p.featured ? "bg-forest text-white border-forest" : "bg-white border-border hover:border-forest"}`}
                    data-testid={`admin-toggle-featured-${p.slug}`}
                  >
                    {p.featured ? "Featured" : "Promote"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "visits" && (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="admin-visits-panel">
            <div className="bg-white border border-border rounded-2xl overflow-hidden">
              <div className="p-4 border-b border-border font-display font-semibold flex items-center gap-2">
                <Eye className="w-4 h-4 text-forest" /> Top pages
              </div>
              {!visits || visits.top_paths.length === 0 ? (
                <div className="p-12 text-center text-ink/50">No visits yet — they'll appear here in real time.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-sand"><tr><th className="text-left p-3">Path</th><th className="text-right p-3 w-24">Visits</th></tr></thead>
                  <tbody className="divide-y divide-border">
                    {visits.top_paths.map((row) => (
                      <tr key={row.path}>
                        <td className="p-3 font-mono text-xs truncate max-w-[300px]">{row.path}</td>
                        <td className="p-3 text-right font-semibold">{row.count.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="bg-white border border-border rounded-2xl overflow-hidden">
              <div className="p-4 border-b border-border font-display font-semibold flex items-center gap-2">
                <Eye className="w-4 h-4 text-forest" /> Recent visits
              </div>
              {!visits || visits.recent.length === 0 ? (
                <div className="p-12 text-center text-ink/50">No visits yet.</div>
              ) : (
                <div className="max-h-[500px] overflow-y-auto divide-y divide-border">
                  {visits.recent.slice(0, 30).map((v) => (
                    <div key={v.id} className="p-3 text-xs flex items-center gap-3">
                      <span className="font-mono truncate flex-1">{v.path}</span>
                      <span className="text-ink/50 shrink-0">{new Date(v.created_at).toLocaleTimeString("en-GB")}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "alerts" && (
          <div className="mt-6 space-y-4" data-testid="admin-alerts-panel">
            <div className="bg-white border border-border rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
              <div className="text-sm text-ink/70">
                <strong className="text-ink">{uniqueEmailCount}</strong> unique subscriber{uniqueEmailCount === 1 ? "" : "s"}
                {alerts.length !== uniqueEmailCount && <span className="text-ink/40"> · {alerts.length} total sign-ups</span>}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => copyEmails("comma")}
                  disabled={uniqueEmailCount === 0}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium bg-forest text-white hover:bg-forest-dark disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="admin-copy-emails-comma"
                  title="Copy as Outlook-friendly comma-separated BCC list"
                >
                  {copied === "comma" ? <><CheckCheck className="w-3.5 h-3.5" />Copied!</> : <><Copy className="w-3.5 h-3.5" />Copy for Outlook BCC</>}
                </button>
                <button
                  onClick={() => copyEmails("newline")}
                  disabled={uniqueEmailCount === 0}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium bg-white border border-border hover:border-forest text-ink disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="admin-copy-emails-newline"
                  title="Copy each email on its own line — handy for spreadsheets"
                >
                  {copied === "newline" ? <><CheckCheck className="w-3.5 h-3.5" />Copied!</> : <><Copy className="w-3.5 h-3.5" />Copy one-per-line</>}
                </button>
              </div>
            </div>

            <div className="bg-sand border border-border rounded-2xl p-4 text-xs text-ink/70 leading-relaxed" data-testid="admin-broadcast-tip">
              <strong className="text-ink">Tip:</strong> In Outlook, open a new email, paste the list into the <strong>BCC</strong> field (so recipients can't see each other's addresses), and put your own address in the <em>To</em> field. This is the legal way to send a blanket update under UK GDPR.
            </div>

            <div className="bg-white border border-border rounded-2xl overflow-hidden" data-testid="admin-alerts-table">
              {alerts.length === 0 ? (
                <div className="p-12 text-center text-ink/50">No email alerts yet. Footer signup feeds this list.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-sand">
                    <tr><th className="text-left p-3">Email</th><th className="text-left p-3">Region</th><th className="text-left p-3">Max £</th><th className="text-left p-3">Created</th></tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {alerts.map((a) => (
                      <tr key={a.id}>
                        <td className="p-3 font-medium">{a.email}</td>
                        <td className="p-3 text-ink/70">{a.region || "—"}</td>
                        <td className="p-3 text-ink/70">{a.max_price || "—"}</td>
                        <td className="p-3 text-ink/60 text-xs">{new Date(a.created_at).toLocaleString("en-GB")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
